import type { MappedProductBoxProps } from '@ori/product-box';
import * as FavoritesUpdatedEvent from '@ori-events/favorites-updated';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';

import { getSetLoadedProductsCallback } from './getSetLoadedProductsCallback';
import { useEventListener } from './useEventListener';

export interface UseRemoveProductOnFavoritesUpdatedEventParams {
  loadedProducts?: MappedProductBoxProps[] | undefined;
  setRemainingProductCodes: Dispatch<SetStateAction<string[]>>;
  setAllowedFavoriteProductCodes: Dispatch<SetStateAction<string[]>>;
  setLoadedProducts: Dispatch<SetStateAction<MappedProductBoxProps[]>>;
  setLoadOffset: Dispatch<SetStateAction<number>>;
  setAmountToLoad?: Dispatch<SetStateAction<number>> | undefined;
}

export const useRemoveProductOnFavoritesUpdatedEvent = ({
  loadedProducts,
  setAllowedFavoriteProductCodes,
  setLoadOffset,
  setLoadedProducts,
  setRemainingProductCodes,
  setAmountToLoad,
}: UseRemoveProductOnFavoritesUpdatedEventParams) => {
  const removeProductFromFavorites = useCallback(
    ({ detail: { favorites, lastUpdated } }: FavoritesUpdatedEvent.Event) => {
      if (lastUpdated && !favorites.includes(lastUpdated)) {
        setRemainingProductCodes((prevCodes) => prevCodes.filter((code) => code !== lastUpdated));
        setAllowedFavoriteProductCodes((prevState) =>
          prevState.filter((productCode) => productCode !== lastUpdated),
        );
        if (
          loadedProducts === undefined ||
          loadedProducts.some((product) => product.code === lastUpdated)
        ) {
          setLoadOffset((previousOffset) => (previousOffset ? previousOffset - 1 : 0));
        }
        setLoadedProducts(getSetLoadedProductsCallback({ lastUpdated, setAmountToLoad }));
      }
    },
    [
      loadedProducts,
      setAllowedFavoriteProductCodes,
      setAmountToLoad,
      setLoadOffset,
      setLoadedProducts,
      setRemainingProductCodes,
    ],
  ) as EventListener;
  useEventListener(FavoritesUpdatedEvent.Name, removeProductFromFavorites);
};
